<template>
    <BaseContainer>
        <span slot="principal">
            <v-content>
                <v-container fluid>

                    <div style="text-align: center; font-weight: bold; font-size: 18px; padding: 15px;">
                        Analytics - Landing Page
                    </div>

                    <!-- Filtros -->
                    <v-row align="center" class="mb-2">
                        <v-col cols="auto">
                            <PeriodoFilter
                                v-model="periodo"
                                defaultPeriodo="Últimos 30 dias"
                                @update="consultar"
                            />
                        </v-col>
                        <v-col cols="auto">
                            <v-select
                                v-model="filtroAnuncio"
                                :items="listaAnuncios"
                                :placeholder="listaAnuncios.length ? 'Todos os anúncios' : 'Nenhum dado no período'"
                                label="Anúncio"
                                outlined
                                dense
                                hide-details
                                clearable
                                style="min-width: 260px;"
                                @change="consultar"
                            />
                        </v-col>
                        <v-col cols="auto">
                            <v-btn color="primary" small @click="consultar">
                                <v-icon left small>mdi-refresh</v-icon>
                                Atualizar
                            </v-btn>
                        </v-col>
                    </v-row>

                    <!-- KPIs -->
                    <v-row>
                        <v-col cols="12" sm="6" md="4" lg="2">
                            <Card
                                :value="String(kpis.total_sessoes)"
                                label="Sessões"
                                color="blue darken-2"
                                icon="mdi-account-multiple"
                            />
                        </v-col>
                        <v-col cols="12" sm="6" md="4" lg="2">
                            <Card
                                :value="String(kpis.total_conversoes)"
                                label="Conversões"
                                color="green darken-2"
                                icon="mdi-check-circle"
                            />
                        </v-col>
                        <v-col cols="12" sm="6" md="4" lg="2">
                            <Card
                                :value="`${kpis.taxa_conversao}%`"
                                label="Taxa de Conversão"
                                color="teal darken-2"
                                icon="mdi-percent"
                            />
                        </v-col>
                        <v-col cols="12" sm="6" md="4" lg="2">
                            <Card
                                :value="formatarTempo(kpis.tempo_medio)"
                                label="Tempo Médio"
                                color="orange darken-2"
                                icon="mdi-timer"
                            />
                        </v-col>
                        <v-col cols="12" sm="6" md="4" lg="2">
                            <Card
                                :value="String(kpis.media_pageviews)"
                                label="Pgs por Sessão"
                                color="purple darken-2"
                                icon="mdi-file-multiple"
                            />
                        </v-col>
                    </v-row>

                    <!-- Gráfico: Sessões por dia -->
                    <v-row class="mt-4">
                        <v-col cols="12" md="8">
                            <v-card>
                                <v-card-title>Sessões e Conversões por Dia</v-card-title>
                                <v-card-text>
                                    <canvas ref="chartSessoesPorDia" height="80"></canvas>
                                </v-card-text>
                            </v-card>
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-card>
                                <v-card-title>Funil de Rotas</v-card-title>
                                <v-card-text>
                                    <canvas ref="chartFunil" height="160"></canvas>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>

                    <!-- Tabela: Sessões por Anúncio -->
                    <v-row class="mt-4">
                        <v-col cols="12">
                            <v-card>
                                <v-card-title>
                                    Performance por Anúncio
                                    <v-spacer />
                                    <span class="caption grey--text">{{ sessoesPorAnuncio.length }} anúncios</span>
                                </v-card-title>
                                <v-data-table
                                    :headers="headersAnuncios"
                                    :items="sessoesPorAnuncio"
                                    :mobileBreakpoint="0"
                                    :items-per-page="15"
                                    dense
                                    sort-by="sessoes"
                                    sort-desc
                                >
                                    <template v-slot:item.taxa_conversao="{ item }">
                                        <v-chip
                                            :color="item.taxa_conversao >= 5 ? 'green' : item.taxa_conversao >= 2 ? 'orange' : 'red'"
                                            dark small
                                        >
                                            {{ item.taxa_conversao }}%
                                        </v-chip>
                                    </template>
                                    <template v-slot:item.tempo_medio="{ item }">
                                        {{ formatarTempo(item.tempo_medio) }}
                                    </template>
                                </v-data-table>
                            </v-card>
                        </v-col>
                    </v-row>

                    <!-- Tabela: Top Cliques + Quiz Drop-off -->
                    <v-row class="mt-4">
                        <v-col cols="12" md="6">
                            <v-card>
                                <v-card-title>Top Botões Clicados</v-card-title>
                                <v-data-table
                                    :headers="headersCliques"
                                    :items="topCliques"
                                    :mobileBreakpoint="0"
                                    :items-per-page="10"
                                    hide-default-footer
                                    dense
                                />
                            </v-card>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-card>
                                <v-card-title>Drop-off do Quiz (Checkout)</v-card-title>
                                <v-data-table
                                    :headers="headersDropoff"
                                    :items="quizDropoff"
                                    :mobileBreakpoint="0"
                                    :items-per-page="20"
                                    hide-default-footer
                                    dense
                                >
                                    <template v-slot:item.tipo="{ item }">
                                        <v-chip
                                            :color="tipoColor(item.tipo)"
                                            dark x-small
                                        >
                                            {{ item.tipo }}
                                        </v-chip>
                                    </template>
                                </v-data-table>
                            </v-card>
                        </v-col>
                    </v-row>

                    <!-- Scroll Depth -->
                    <v-row class="mt-4">
                        <v-col cols="12" md="5">
                            <v-card>
                                <v-card-title>Scroll Depth Médio por Página</v-card-title>
                                <v-data-table
                                    :headers="headersScroll"
                                    :items="scrollDepth"
                                    :mobileBreakpoint="0"
                                    hide-default-footer
                                    :items-per-page="-1"
                                    dense
                                >
                                    <template v-slot:item.profundidade_media="{ item }">
                                        <v-progress-linear
                                            :value="item.profundidade_media"
                                            :color="item.profundidade_media >= 75 ? 'green' : item.profundidade_media >= 50 ? 'orange' : 'red'"
                                            height="16"
                                            rounded
                                        >
                                            <span style="font-size: 11px; color: white;">{{ item.profundidade_media }}%</span>
                                        </v-progress-linear>
                                    </template>
                                </v-data-table>
                            </v-card>
                        </v-col>
                        <v-col cols="12" md="7">
                            <v-card>
                                <v-card-title>Tempo Médio por Rota (segundos)</v-card-title>
                                <v-card-text>
                                    <canvas ref="chartTempoRota" height="160"></canvas>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>

                    <!-- Lista de Sessões -->
                    <v-row class="mt-4">
                        <v-col cols="12">
                            <v-card>
                                <v-card-title>
                                    Sessões Recentes
                                    <v-spacer />
                                    <v-select
                                        v-model="filtroConverteu"
                                        :items="[
                                            { text: 'Todas', value: null },
                                            { text: 'Convertidas', value: true },
                                            { text: 'Não convertidas', value: false },
                                        ]"
                                        item-text="text"
                                        item-value="value"
                                        label="Status"
                                        outlined dense hide-details
                                        style="max-width: 180px;"
                                        @change="consultarSessoes"
                                    />
                                </v-card-title>
                                <v-data-table
                                    :headers="headersSessoes"
                                    :items="sessoes"
                                    :mobileBreakpoint="0"
                                    :server-items-length="sessoesMeta.total"
                                    :options.sync="opcoesSessoes"
                                    :loading="carregandoSessoes"
                                    dense
                                    @click:row="abrirSessao"
                                    style="cursor: pointer;"
                                >
                                    <template v-slot:item.converteu="{ item }">
                                        <v-icon :color="item.converteu ? 'green' : 'grey'" small>
                                            {{ item.converteu ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                                        </v-icon>
                                    </template>
                                    <template v-slot:item.tempo_total_segundos="{ item }">
                                        {{ formatarTempo(item.tempo_total_segundos) }}
                                    </template>
                                    <template v-slot:item.anuncio_uuid="{ item }">
                                        <span class="caption">{{ item.anuncio_uuid || '—' }}</span>
                                    </template>
                                    <template v-slot:item.atribuicao_fonte="{ item }">
                                        <v-chip
                                            v-if="item.atribuicao_fonte"
                                            x-small
                                            dark
                                            :color="corDaFonte(item.atribuicao_fonte)"
                                        >
                                            {{ rotuloDaFonte(item.atribuicao_fonte) }}
                                        </v-chip>
                                        <span v-else class="caption grey--text">—</span>
                                    </template>
                                    <template v-slot:item.created_at="{ item }">
                                        {{ formatDate(item.created_at) }}
                                    </template>
                                </v-data-table>
                            </v-card>
                        </v-col>
                    </v-row>

                    <!-- Dialog: Detalhe da Sessão -->
                    <v-dialog v-model="dialogSessao" max-width="750" scrollable>
                        <v-card v-if="sessaoDetalhe">
                            <v-card-title>
                                Sessão {{ sessaoDetalhe.sessao && sessaoDetalhe.sessao.uuid }}
                                <v-spacer />
                                <v-btn icon @click="dialogSessao = false">
                                    <v-icon>mdi-close</v-icon>
                                </v-btn>
                            </v-card-title>
                            <v-divider />
                            <v-card-text v-if="sessaoDetalhe.sessao" class="pt-3">
                                <v-row dense>
                                    <v-col cols="6" sm="4">
                                        <div class="caption grey--text">Anúncio</div>
                                        <div>{{ sessaoDetalhe.sessao.anuncio_uuid || '—' }}</div>
                                    </v-col>
                                    <v-col cols="6" sm="4">
                                        <div class="caption grey--text">UTM</div>
                                        <div>{{ sessaoDetalhe.sessao.utm || '—' }}</div>
                                    </v-col>
                                    <v-col cols="6" sm="4">
                                        <div class="caption grey--text">Procedência</div>
                                        <div>
                                            <v-chip
                                                v-if="sessaoDetalhe.sessao.atribuicao_fonte"
                                                x-small
                                                dark
                                                :color="corDaFonte(sessaoDetalhe.sessao.atribuicao_fonte)"
                                            >
                                                {{ rotuloDaFonte(sessaoDetalhe.sessao.atribuicao_fonte) }}
                                            </v-chip>
                                            <span v-else>—</span>
                                        </div>
                                    </v-col>
                                    <v-col cols="6" sm="4">
                                        <div class="caption grey--text">Lead</div>
                                        <div class="caption">
                                            <template v-if="sessaoDetalhe.sessao.contato_uuid">
                                                {{ sessaoDetalhe.sessao.contato_uuid }}
                                                <div class="grey--text">
                                                    vínculo: {{ sessaoDetalhe.sessao.vinculo_fonte || '—' }}
                                                </div>
                                            </template>
                                            <span v-else>—</span>
                                        </div>
                                    </v-col>
                                    <v-col cols="6" sm="4">
                                        <div class="caption grey--text">Tempo Total</div>
                                        <div>{{ formatarTempo(sessaoDetalhe.sessao.tempo_total_segundos) }}</div>
                                    </v-col>
                                    <v-col cols="6" sm="4">
                                        <div class="caption grey--text">Converteu</div>
                                        <div>
                                            <v-chip :color="sessaoDetalhe.sessao.converteu ? 'green' : 'grey'" dark x-small>
                                                {{ sessaoDetalhe.sessao.converteu ? 'Sim' : 'Não' }}
                                            </v-chip>
                                        </div>
                                    </v-col>
                                    <v-col cols="6" sm="4">
                                        <div class="caption grey--text">Pageviews</div>
                                        <div>{{ sessaoDetalhe.sessao.total_pageviews }}</div>
                                    </v-col>
                                    <v-col cols="6" sm="4">
                                        <div class="caption grey--text">Entrada</div>
                                        <div>{{ sessaoDetalhe.sessao.primeiro_path || '—' }}</div>
                                    </v-col>
                                </v-row>

                                <div class="subtitle-2 mt-4 mb-2">Linha do Tempo</div>
                                <v-timeline dense>
                                    <v-timeline-item
                                        v-for="(ev, i) in sessaoDetalhe.eventos"
                                        :key="i"
                                        :color="corEvento(ev.tipo)"
                                        :icon="iconeEvento(ev.tipo)"
                                        small
                                    >
                                        <div class="caption">
                                            <strong>{{ ev.tipo }}</strong>
                                            <span v-if="ev.label"> — {{ ev.label }}</span>
                                        </div>
                                        <div class="caption grey--text">
                                            {{ formatDate(ev.created_at) }}
                                            <span v-if="ev.tempo_na_rota_segundos">
                                                ({{ ev.tempo_na_rota_segundos }}s na rota {{ ev.rota }})
                                            </span>
                                        </div>
                                        <div v-if="ev.dados && Object.keys(ev.dados).length" class="caption mt-1">
                                            <v-chip
                                                v-for="(val, key) in ev.dados"
                                                :key="key"
                                                x-small outlined class="mr-1 mb-1"
                                            >
                                                {{ key }}: {{ val }}
                                            </v-chip>
                                        </div>
                                    </v-timeline-item>
                                </v-timeline>
                            </v-card-text>
                        </v-card>
                    </v-dialog>

                </v-container>
            </v-content>
        </span>
    </BaseContainer>
</template>

<script>
import BaseContainer from '@/components/BaseContainer.vue';
import Card from '@/views/home/Card.vue';
import PeriodoFilter from '@/components/PeriodoFilter.vue';
import Chart from 'chart.js';
import moment from 'moment';

export default {
    name: 'AnalyticsLp',
    components: { BaseContainer, Card, PeriodoFilter },

    data: () => ({
        periodo: {},
        filtroAnuncio: null,
        filtroConverteu: null,

        kpis: {
            total_sessoes:    0,
            total_conversoes: 0,
            taxa_conversao:   0,
            tempo_medio:      0,
            media_pageviews:  0,
        },
        sessoesPorDia:     [],
        sessoesPorAnuncio: [],
        topCliques:        [],
        funilRotas:        [],
        quizDropoff:       [],
        scrollDepth:       [],

        // Lista de sessões
        sessoes:           [],
        sessoesMeta:       { total: 0 },
        carregandoSessoes: false,
        opcoesSessoes:     { page: 1, itemsPerPage: 25 },

        // Anúncios p/ filtro
        listaAnuncios: [],

        // Detalhe de sessão
        dialogSessao:   false,
        sessaoDetalhe:  null,

        charts: {},

        headersAnuncios: [
            { text: 'Anúncio',        value: 'anuncio',         sortable: true },
            { text: 'Sessões',        value: 'sessoes',         sortable: true },
            { text: 'Conversões',     value: 'conversoes',      sortable: true },
            { text: 'Taxa Conv.',     value: 'taxa_conversao',  sortable: true },
            { text: 'Tempo Médio',    value: 'tempo_medio',     sortable: true },
            { text: 'Pgs/Sessão',     value: 'media_pageviews', sortable: true },
        ],
        headersCliques: [
            { text: 'Botão / Label', value: 'label',  sortable: true },
            { text: 'Rota',          value: 'rota',   sortable: true },
            { text: 'Cliques',       value: 'total',  sortable: true },
        ],
        headersDropoff: [
            { text: 'Tipo',     value: 'tipo',    sortable: false },
            { text: 'Pergunta', value: 'label',   sortable: false },
            { text: 'Sessões',  value: 'sessoes', sortable: true },
        ],
        headersScroll: [
            { text: 'Rota',               value: 'rota',                sortable: false },
            { text: 'Profundidade Média', value: 'profundidade_media',  sortable: true },
        ],
        headersSessoes: [
            { text: 'Data',         value: 'created_at',          sortable: false },
            { text: 'Anúncio',      value: 'anuncio_uuid',        sortable: false },
            { text: 'UTM',          value: 'utm',                 sortable: false },
            { text: 'Procedência',  value: 'atribuicao_fonte',    sortable: false },
            { text: 'Tempo',        value: 'tempo_total_segundos',sortable: false },
            { text: 'Pageviews',    value: 'total_pageviews',     sortable: false },
            { text: 'Converteu',    value: 'converteu',           sortable: false },
            { text: 'Entrada',      value: 'primeiro_path',       sortable: false },
        ],
    }),

    watch: {
        opcoesSessoes: {
            deep: true,
            handler() {
                this.consultarSessoes();
            },
        },
    },

    mounted() {
        this.$nextTick(() => this.consultar());
    },

    methods: {
        async consultar() {
            const params = this.buildParams();

            try {
                const resp = await this.$http.get(`lp-tracking/dashboard?${params}`);
                const d = resp.data || resp;

                this.kpis              = d.kpis;
                this.sessoesPorDia     = d.sessoes_por_dia;
                this.sessoesPorAnuncio = d.sessoes_por_anuncio;
                this.topCliques        = d.top_cliques;
                this.funilRotas        = d.funil_rotas;
                this.quizDropoff       = d.quiz_dropoff;
                this.scrollDepth       = d.scroll_depth;

                // Atualiza a lista de anúncios para o filtro a partir dos dados já carregados
                this.listaAnuncios = (d.sessoes_por_anuncio || [])
                    .filter(a => a.anuncio && a.anuncio !== 'direto')
                    .map(a => a.anuncio);

                this.$nextTick(() => this.gerarGraficos());
            } catch (e) {
                console.error(e);
            }

            this.consultarSessoes();
        },

        async consultarSessoes() {
            this.carregandoSessoes = true;
            try {
                const params = this.buildParams()
                    + `&per_page=${this.opcoesSessoes.itemsPerPage}`
                    + `&page=${this.opcoesSessoes.page}`
                    + (this.filtroConverteu !== null ? `&converteu=${this.filtroConverteu}` : '');

                const resp = await this.$http.get(`lp-tracking/sessoes?${params}`);
                const d = resp.data || resp;
                this.sessoes     = d.data || [];
                this.sessoesMeta = d.meta || { total: d.total || 0 };
            } catch (e) {
                console.error(e);
            } finally {
                this.carregandoSessoes = false;
            }
        },

        async abrirSessao(sessao) {
            try {
                const resp = await this.$http.get(`lp-tracking/sessoes/${sessao.uuid}`);
                this.sessaoDetalhe = resp.data || resp;
                this.dialogSessao = true;
            } catch (e) {
                console.error(e);
            }
        },

        buildParams() {
            let p = `de=${this.periodo.start || ''}&ate=${this.periodo.end || ''}`;
            if (this.filtroAnuncio) p += `&ad=${this.filtroAnuncio}`;
            return p;
        },

        gerarGraficos() {
            // Sessões e conversões por dia
            const dias = [...this.sessoesPorDia];
            this.renderChart('chartSessoesPorDia', {
                type: 'line',
                labels: dias.map(d => d.dia),
                datasets: [
                    {
                        label: 'Sessões',
                        data: dias.map(d => d.total),
                        borderColor: 'rgba(33, 150, 243, 1)',
                        backgroundColor: 'rgba(33, 150, 243, 0.1)',
                        fill: true,
                    },
                    {
                        label: 'Conversões',
                        data: dias.map(d => d.conversoes),
                        borderColor: 'rgba(76, 175, 80, 1)',
                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        fill: true,
                    },
                ],
            });

            // Funil de rotas (bar horizontal)
            const rotas = [...this.funilRotas].sort((a, b) => b.sessoes - a.sessoes);
            this.renderChart('chartFunil', {
                type: 'horizontalBar',
                labels: rotas.map(r => r.rota || '/'),
                datasets: [{
                    label: 'Sessões',
                    data: rotas.map(r => r.sessoes),
                    backgroundColor: 'rgba(103, 58, 183, 0.7)',
                }],
            }, { scales: { xAxes: [{ ticks: { beginAtZero: true } }] } });

            // Tempo médio por rota
            const rotasTempo = [...this.funilRotas].filter(r => r.tempo_medio > 0);
            this.renderChart('chartTempoRota', {
                type: 'bar',
                labels: rotasTempo.map(r => r.rota || '/'),
                datasets: [{
                    label: 'Segundos médios',
                    data: rotasTempo.map(r => r.tempo_medio),
                    backgroundColor: 'rgba(255, 152, 0, 0.7)',
                }],
            });
        },

        renderChart(ref, config, extraOptions = {}) {
            if (this.charts[ref]) this.charts[ref].destroy();
            const ctx = this.$refs[ref];
            if (!ctx) return;

            this.charts[ref] = new Chart(ctx, {
                type: config.type,
                data: {
                    labels: config.labels,
                    datasets: config.datasets,
                },
                options: {
                    responsive: true,
                    legend: { position: 'bottom' },
                    ...extraOptions,
                },
            });
        },

        formatarTempo(segundos) {
            if (!segundos) return '0s';
            const m = Math.floor(segundos / 60);
            const s = segundos % 60;
            return m > 0 ? `${m}m ${s}s` : `${s}s`;
        },

        /**
         * Procedência da atribuição: 'url' é clique de verdade no link do anúncio,
         * 'storage' é resgate de uma visita anterior (o visitante voltou sem os
         * parâmetros). Somar os dois no mesmo número seria inflar a campanha.
         */
        corDaFonte(fonte) {
            const cores = {
                url: 'primary',
                storage: 'deep-purple',
                referrer: 'teal',
                direto: 'grey',
            };

            return cores[fonte] || 'grey';
        },

        rotuloDaFonte(fonte) {
            const rotulos = {
                url: 'clique no link',
                storage: 'resgatado',
                referrer: 'referrer',
                direto: 'direto',
            };

            return rotulos[fonte] || fonte;
        },

        formatDate(date) {
            if (!date) return '—';
            return moment(date).format('DD/MM/YY HH:mm');
        },

        formatPrice(val) {
            return Number(val || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
        },

        tipoColor(tipo) {
            const map = {
                quiz_inicio:    'blue',
                quiz_resposta:  'orange',
                quiz_completo:  'green',
            };
            return map[tipo] || 'grey';
        },

        corEvento(tipo) {
            const map = {
                pageview:       'blue',
                click:          'purple',
                quiz_inicio:    'teal',
                quiz_resposta:  'orange',
                quiz_completo:  'green',
                lead_criado:    'success',
                scroll_depth:   'grey',
            };
            return map[tipo] || 'grey lighten-1';
        },

        iconeEvento(tipo) {
            const map = {
                pageview:       'mdi-eye',
                click:          'mdi-cursor-default-click',
                quiz_inicio:    'mdi-play',
                quiz_resposta:  'mdi-comment-question',
                quiz_completo:  'mdi-check',
                lead_criado:    'mdi-account-plus',
                scroll_depth:   'mdi-arrow-down',
            };
            return map[tipo] || 'mdi-circle-small';
        },
    },
};
</script>
